import { useCallback, useMemo } from 'react'
import {
  getNestedOrFloatingElementById,
  getPersonName,
  getSexEnumFromResource,
  getRelationshipToPrimaryPersonDisplayLabel,
} from '@fs/zion-sls'

/**
 * Helper function to get gender value from a person object
 * @param {Object} person - The person object
 * @returns {string} Gender value or 'unknown'
 */
const getPersonGender = (person) => {
  if (!person) return 'unknown'

  const genderField = person?.subElements?.find((field) => field.fieldType === 'SEX_CODE')
  return genderField ? getSexEnumFromResource(genderField) : 'unknown'
}

/**
 * Helper function to find relationship for a person
 * @param {Object} person - The person object
 * @param {Array} allRecords - All records data
 * @param {Object} state - The SLS state context
 * @returns {string} Relationship type
 */
const findPersonRelationship = (person, allRecords, state) => {
  if (!person || !allRecords) return ''

  if (person.primary) return 'Primary'

  const relationship = allRecords
    .filter((record) => record?.subElements)
    .map((record) => {
      record.subElements.filter((sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === state.currentImageArk)
      return getRelationshipToPrimaryPersonDisplayLabel(person, state.types, record)
    })
    .find((rel) => rel !== '')

  return relationship || 'Record'
}

/**
 * Helper function to handle person selection logic
 * @param {Object} person - The person to select
 * @param {Function} r2Dispatch - R2 dispatch function
 */
const handlePersonSelection = (person, r2Dispatch) => {
  if (!person) return

  let personId = person?.id
  if (personId?.key) {
    personId = personId.key
  }

  const selectedId = personId === 'seeAll' ? '' : personId
  r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: selectedId })
}

/**
 * Custom hook to manage person selection logic and derived person data
 * @param {Object} currentImage - The current image object
 * @param {Object} state - The SLS state context
 * @param {Array} allRecords - All records data
 * @param {string} selectedPersonId - Currently selected person ID
 * @param {Function} r2Dispatch - R2 dispatch function
 * @param {Function} t - Translation function
 * @returns {Object} Person selection utilities and derived data
 */
export const usePersonSelector = (currentImage, state, allRecords, selectedPersonId, r2Dispatch, t) => {
  // Get the selected person object
  const selectedPerson = useMemo(
    () => getNestedOrFloatingElementById([currentImage], selectedPersonId),
    [currentImage, selectedPersonId]
  )

  // Get gender value from person
  const genderValue = useMemo(() => getPersonGender(selectedPerson), [selectedPerson])

  // Process relationship for the selected person
  const relationshipType = useMemo(
    () => findPersonRelationship(selectedPerson, allRecords, state),
    [selectedPerson, allRecords, state]
  )

  // Person name
  const personName = useMemo(() => (selectedPerson ? getPersonName({ person: selectedPerson }) : ''), [selectedPerson])

  // Title for the person
  const personTitle = useMemo(() => {
    if (!selectedPerson) return ''

    const name = getPersonName({ person: selectedPerson })
    const principalSuffix = selectedPerson.primary
      ? ` ${t('history-viewer.principal-person.label', '(Principal)')}`
      : ''
    return `${name}${principalSuffix}`
  }, [selectedPerson, t])

  // Select person callback
  const selectPerson = useCallback((person) => handlePersonSelection(person, r2Dispatch), [r2Dispatch])

  return {
    selectedPerson,
    genderValue,
    relationshipType,
    personName,
    personTitle,
    selectPerson,
  }
}
